<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-card v-if="!dataLoaded">
          <v-card-title>Program Change Form (Loading...)</v-card-title>
          <v-card-text>Loading your program change form. This should only take a few seconds.</v-card-text>
        </v-card>
        <template v-else>
          <v-card>
            <v-toolbar flat>
              <v-toolbar-title>Program Change Status: {{ termLabel }}</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <h3>Current Status: {{ status }}</h3>
              <template v-if="status === 'Returned'">
                <p>Your request has been returned for you to make changes. Please review the request timeline at the bottom of the page or your email for why it was returned and then click this link below to open the request form to make further changes. Your current changes will load into that request.</p>
                <v-btn color="info" to="/student/program-change">Access Program Change Form</v-btn>
              </template>
              <p v-else-if="status === 'Submitted'">You have submitted your change request. The Records office will do an initial review of this request, and will adjust the advisors as needed. Once that is finished, the request will be sent to your advisors for approval.</p>
              <p v-else-if="status === 'Pending'">Your request is pending approval from your advisor(s). If they have questions they may ask using the timeline below or via email.</p>
            </v-card-text>
          </v-card>
          <h3 class="mt-4 mb-2">Advisor Approvals</h3>
          <v-list>
            <v-list-item v-for="{ pidm, name, approved } of advisorApproval" :key="pidm">
              <v-list-item-content>
                <v-list-item-title>{{ name }}</v-list-item-title>
                <v-list-item-subtitle>{{ approved ? 'Approved' : 'Not Approved' }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon v-if="approved" color="success">fas fa-check-circle</v-icon>
                <v-icon v-else color="info">fas fa-question-circle</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <h3 class="mt-4 mb-2">Requested Changes:</h3>
          <v-alert v-for="{ action, field, value, prev, advisorName, concentrations, changeText } of myChanges" :key="action + '-' + field + '-' + value" :color="action === 'add' ? 'success' : (action === 'drop' ? 'error' : 'info')" colored-border border="left">
            <template #prepend>
              <v-icon v-if="action === 'add'" class="mr-4">fas fa-plus-circle</v-icon>
              <v-icon v-else-if="action === 'drop'" class="mr-4">fas fa-minus-circle</v-icon>
              <v-icon v-else class="mr-4">fas fa-exchange</v-icon>
            </template>
            {{ changeText }}
          </v-alert>
          <v-card v-if="showAddMessage">
            <v-card-text>
              <h3>Add a Message</h3>
              <v-textarea v-model="comment" label="Message" style="margin-top:1em" rows="3" outlined hide-details></v-textarea>
              <v-checkbox v-model="emailAdvisors" label="Email this message to my advisor(s)" hide-details />
              <v-checkbox v-model="emailRecords" label="Email this message to the Records Office" hide-details />
            </v-card-text>
            <v-card-actions>
              <v-btn :disabled="comment === '' || isSending" @click="addComment" color="info">{{ isSending ? (emailAdvisors || emailRecords ? 'Sending Emails...' : 'Saving Message...') : (emailAdvisors || emailRecords ? 'Email Message' : 'Add Message') }}</v-btn>
              <v-spacer></v-spacer>
              <v-btn @click="showAddMessage = false">Cancel</v-btn>
            </v-card-actions>
          </v-card>
          <v-btn v-else @click="showAddMessage = true">Add Message</v-btn>
          <h3 style="margin-top:1em">Change Request Timeline</h3>
          <v-timeline dense>
            <v-timeline-item v-for="({ name, date, text, icon, color }, index) in timeline" :key="'timeline-' + index" :color="color">
              <template v-slot:icon>
                <v-icon small color="white">{{ icon }}</v-icon>
              </template>
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>{{ name }}</v-col>
                    <v-col class="text-right">{{ stringFormatDate(date) }}</v-col>
                  </v-row>
                  <v-divider style="margin: .2em 0"></v-divider>
                  <p style="margin-bottom:0" v-html="text"></p>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { onMounted, ref, computed, reactive, watch } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'

export default {
  setup (props, { root }) {
    const service = root.$feathers.service('student/major-change')
    const settingServ = root.$feathers.service('student/reg-setting')

    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const dataLoaded = ref(false)
    const id = ref('')
    const term = ref('')
    const termLabel = ref('')
    const status = ref('')
    const submission = reactive({})
    const timeline = ref([])
    const advisorApproval = ref([])

    const changes = ref([])
    const myChanges = ref([])
    watch(changes, async () => {
      const arr = []
      for (const rec of changes.value) {
        const { action, field, value, prev, advisor, advisorName, concentrations } = rec
        let changeText = action.substring(0, 1).toUpperCase() + action.substring(1) + ' ' + field.substring(0, 1).toUpperCase() + field.substring(1)
        if (action === 'change') {
          changeText += ` from ${field === 'advisor' ? await getAdvrName(prev) : await lookupValue(field, prev)} to`
        }
        changeText += ': ' + (field === 'advisor' ? advisorName : await lookupValue(field, value))
        if (field === 'major') {
          changeText += ' with '
          if (advisorName) changeText += ' Advisor: ' + advisorName
          else changeText += ' Advisor: ' + await getAdvrName(advisor)
          if (concentrations && concentrations.length > 0) {
            if (advisorName) changeText += ' and'
            const temp = []
            for (const conc of concentrations) {
              temp.push(await lookupValue('concentration', conc))
            }
            changeText += ' Concentrations: ' + temp.join('; ')
          }
        }
        arr.push({ ...rec, changeText })
      }
      myChanges.value = arr
    })

    async function getAdvrName (pidm) {
      const { data: dirData } = await root.$feathers.service('directory/people').find({ query: { pidm } })
      if (dirData.length > 0) {
        return dirData[0].name.last + ', ' + dirData[0].name.first
      }
      return 'Advisor Not Found'
    }
    async function lookupValue (category, value) {
      console.log('Looking up value')
      const aggregate = [
        { $match: {
          form: 'program',
          category
        } },
        { $unwind: '$options' },
        { $match: {
          'options.value': Array.isArray(value) ? { $in: value } : value
        } },
        { $project: {
          value: '$options.value',
          text: '$options.text'
        } }
      ]
      const { data } = await settingServ.find({ query: { aggregate } })
      if (data.length > 0) return data[0].text
      else console.log('Nothing found in lookup')
    }

    const majorItems = ref([])
    const minorItems = ref([])

    onMounted(async () => {
      // Load the PIDM of the current user
      let pidm = user.value.pidm
      // Get possible terms from the term warehouse
      let { data: termData } = await root.$feathers.service('system/term').find({ query: { end: { $gt: new Date() } } })
      let terms = []
      for (let i = 0; i < termData.length; i++) {
        terms.push(termData[i].term)
      }
      // Load the student's academic data from the student/term-detail
      let { data: studentData } = await root.$feathers.service('student/term-detail').find({ query: { pidm, term: { $in: terms } } })
      if (studentData.length > 0) {
        let { term: termCode } = studentData[0]
        term.value = termCode
        termData.forEach(({ term, label }) => { if (term === termCode) termLabel.value = label })
        dataLoaded.value = true
      } else {
        root.$store.dispatch('main/snackbar', { active: true, color: 'error', timeout: 10000, text: 'No enrollment details were found for you in any active term. Please contact the Records office for assistance.' })
        root.$router.replace('/')
      }
      // Check to see if the student has a pending submission; if not, redirect them to the form
      let { data: changeData } = await service.find({ query: { pidm, term: term.value } })
      if (changeData.length === 0) {
        root.$router.replace('/student/program-change/')
      } else {
        id.value = changeData[0]._id
        status.value = changeData[0].status
        changes.value = changeData[0].changes
        timeline.value = changeData[0].timeline.filter(({ visibleToStudent }) => !!visibleToStudent).sort((a, b) => { return new Date(a.date) < new Date(b.date) ? 1 : -1 })
        advisorApproval.value = changeData[0].advisorApproval
      }
      dataLoaded.value = true
    })

    const showAddMessage = ref(false)
    const comment = ref('')
    const emailAdvisors = ref(false)
    const emailRecords = ref(false)
    const isSending = ref(false)
    async function addComment () {
      isSending.value = true
      const obj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: comment.value,
        icon: 'fal fa-comment',
        color: 'info',
        visibleToStudent: true
      }
      await service.patch(id.value, { $push: { timeline: obj } })
      const to = []
      if (emailRecords.value) to.push('recordsoffice@covenant.edu')
      if (emailAdvisors.value) {
        const pidms = advisorApproval.value.map(({ pidm }) => pidm)
        const { data } = await root.$feathers.service('directory/people').find({ query: { pidm: { $in: pidms }, $select: ['email'] } })
        data.forEach(({ email }) => to.push(email))
      }
      if (to.length > 0) {
        await root.$feathers.service('system/email').create({
          to: 'jon.moon@covenant.edu',
          from: 'report-email@covenant.edu',
          subject: 'Student Message Re: Program/Advisor Change',
          html: `${user.value.name} just added a message to their program/advisor change submission. Here's their message:<br/><br/>Here is the message:<br/><br/><div style="border:2px solid #EEE;margin-bottom:20px;"><div style="border:2px solid #DDD"><div style="border:1px solid #CCC;padding:24px;">${comment.value}</div></div></div><a href="https://portal.covenant.edu/student/program-change/admin/${id.value}" style="padding:12px;background-color:#193264 !important;text-decoration:none;color:white;height:36px;">View this request in the Portal</a>`
        })
      }
      timeline.value.splice(0, 0, obj)
      showAddMessage.value = false
      comment.value = false
      emailAdvisors.value = false
      emailRecords.value = false

      isSending.value = false
    }

    return {
      user,
      dataLoaded,
      term,
      termLabel,
      status,
      submission,
      timeline,
      advisorApproval,
      changes,
      myChanges,
      majorItems,
      minorItems,
      showAddMessage,
      comment,
      emailAdvisors,
      emailRecords,
      isSending,
      addComment,
      stringFormatDate
    }
  }
}
</script>
